import {
  Badge,
  Box,
  Button,
  Container,
  Grid,
  HStack,
  Icon,
  Input,
  Text,
  Textarea,
  VStack,
  useDisclosure
} from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheck,
  FiCheckCircle,
  FiChevronDown,
  FiEdit,
  FiExternalLink,
  FiHash,
  FiInfo,
  FiLink,
  FiLoader,
  FiNavigation,
  FiRefreshCw,
  FiSearch,
  FiShuffle
} from "react-icons/fi";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { mockApi } from "../api/mockApi";
import { useEffect } from "react";
import { ABTestPaths } from "../components/CreateLink/ABTestPaths";

// URL validation function
const validateUrl = (url: string): { isValid: boolean; error?: string } => {
  if (!url) {
    return { isValid: false, error: "URL is required" };
  }
  
  try {
    const urlObj = new URL(url);
    
    // Check for valid protocol
    if (!['http:', 'https:'].includes(urlObj.protocol)) {
      return { isValid: false, error: "URL must start with http:// or https://" };
    }
    
    // Check for valid hostname
    if (!urlObj.hostname || urlObj.hostname.length < 3) {
      return { isValid: false, error: "Invalid domain name" };
    }
    
    // Check for common typos
    if (urlObj.hostname.includes('..') || urlObj.hostname.startsWith('.') || urlObj.hostname.endsWith('.')) {
      return { isValid: false, error: "Invalid domain format" };
    }
    
    return { isValid: true };
  } catch (error) {
    return { isValid: false, error: "Invalid URL format. Please enter a valid URL (e.g., https://example.com)" };
  }
};

interface FormData {
  domain: string;
  destinationType: "single" | "ab-test" | "variant" | "custom";
  destinationPath: string;
  destinationVariant: string;
  customRedirectUrl: string;
  abTestPaths: Array<{
    id: string;
    type: "path" | "variant";
    value: string;
    percentage: number;
  }>;
  abTestSchedule: {
    enabled: boolean;
    startDate: string;
    endDate: string;
  };
}

// Available paths for dropdowns
const availablePaths = [
  { value: "/buyer/home", label: "/buyer/home", description: "Buyer homepage" },
  {
    value: "/buyer/search",
    label: "/buyer/search",
    description: "Property search"
  },
  {
    value: "/buyer/favorites",
    label: "/buyer/favorites",
    description: "Saved properties"
  },
  {
    value: "/seller/home",
    label: "/seller/home",
    description: "Seller homepage"
  },
  {
    value: "/seller/valuation",
    label: "/seller/valuation",
    description: "Home valuation tool"
  },
  {
    value: "/seller/guide",
    label: "/seller/guide",
    description: "Selling guide"
  },
  {
    value: "/agent/dashboard",
    label: "/agent/dashboard",
    description: "Agent portal"
  },
  {
    value: "/agent/listings",
    label: "/agent/listings",
    description: "Manage listings"
  },
  { value: "/resources", label: "/resources", description: "Resources page" },
  { value: "/blog", label: "/blog", description: "Blog homepage" },
  { value: "/about", label: "/about", description: "About us" },
  { value: "/contact", label: "/contact", description: "Contact page" },
  { value: "/", label: "/", description: "Homepage" }
];

// Available website variants
const availableVariants = [
  {
    value: "54424264261",
    label: "54424264261",
    description: "Homepage - Version A"
  },
  {
    value: "54424264262",
    label: "54424264262",
    description: "Homepage - Version B"
  },
  {
    value: "1754424264261",
    label: "1754424264261",
    description: "Buyer Landing - V1"
  },
  {
    value: "1754424264262",
    label: "1754424264262",
    description: "Buyer Landing - V2"
  },
  {
    value: "2854424264261",
    label: "2854424264261",
    description: "Seller Landing - V1"
  },
  {
    value: "2854424264262",
    label: "2854424264262",
    description: "Seller Landing - V2"
  },
  {
    value: "3954424264261",
    label: "3954424264261",
    description: "Property Search - New"
  },
  {
    value: "3954424264262",
    label: "3954424264262",
    description: "Property Search - Classic"
  },
  {
    value: "4054424264261",
    label: "4054424264261",
    description: "Agent Portal - Standard"
  },
  {
    value: "4054424264262",
    label: "4054424264262",
    description: "Agent Portal - Premium"
  },
  {
    value: "5154424264261",
    label: "5154424264261",
    description: "Mobile Optimized - V1"
  },
  {
    value: "5154424264262",
    label: "5154424264262",
    description: "Mobile Optimized - V2"
  }
];

// Searchable Dropdown Component
interface SearchableDropdownProps {
  value: string;
  placeholder: string;
  options: any[];
  isOpen: boolean;
  onToggle: () => void;
  onSelect: (value: string) => void;
  searchQuery: string;
  onSearchChange: (value: string) => void;
  disabled: boolean;
  isMonospace?: boolean;
}

const SearchableDropdown: React.FC<SearchableDropdownProps> = ({
  value,
  placeholder,
  options,
  isOpen,
  onToggle,
  onSelect,
  searchQuery,
  onSearchChange,
  disabled,
  isMonospace = false
}) => (
  <>
    <Box
      as="button"
      w="100%"
      p={3}
      bg="white"
      border="1px solid"
      borderColor={isOpen ? "blue.500" : "gray.300"}
      borderRadius="md"
      textAlign="left"
      fontSize="md"
      aria-disabled={disabled}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onToggle();
      }}
      _hover={{ borderColor: "gray.400" }}
      _focus={{
        borderColor: "blue.500",
        boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
        outline: "none"
      }}
      _disabled={{
        opacity: 0.6,
        cursor: "not-allowed"
      }}
    >
      <HStack justify="space-between">
        <Text
          color={value ? "black" : "gray.500"}
          fontFamily={isMonospace && value ? "mono" : "inherit"}
        >
          {value || placeholder}
        </Text>
        <Icon
          as={FiChevronDown}
          transform={isOpen ? "rotate(180deg)" : "rotate(0deg)"}
          transition="transform 0.2s"
        />
      </HStack>
    </Box>

    {isOpen && (
      <Box
        position="absolute"
        top="100%"
        left={0}
        right={0}
        mt={1}
        bg="white"
        border="1px solid"
        borderColor="gray.200"
        borderRadius="md"
        shadow="lg"
        zIndex={10}
        maxH="400px"
        overflow="hidden"
      >
        <Box p={3} borderBottom="1px solid" borderColor="gray.200">
          <HStack>
            <Icon as={FiSearch} color="gray.400" />
            <Input
              placeholder={`Search ${isMonospace ? "variants" : "paths"}...`}
              value={searchQuery}
              onChange={(e) => onSearchChange(e.target.value)}
              size="sm"
              border="none"
              _focus={{
                outline: "none",
                boxShadow: "none"
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </HStack>
        </Box>

        <Box maxH="300px" overflowY="auto">
          {options.length > 0 ? (
            options.map((option: any) => (
              <Box
                key={option.value}
                as="button"
                w="100%"
                p={3}
                textAlign="left"
                bg={value === option.value ? "blue.50" : "white"}
                _hover={{ bg: "gray.50" }}
                onClick={(e) => {
                  e.stopPropagation();
                  onSelect(option.value);
                }}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <HStack justify="space-between" align="start">
                  <VStack align="start" gap={0}>
                    <Text
                      fontSize="sm"
                      fontWeight={value === option.value ? "medium" : "normal"}
                      fontFamily={isMonospace ? "mono" : "inherit"}
                    >
                      {option.label}
                    </Text>
                    <Text fontSize="xs" color="gray.500">
                      {option.description}
                    </Text>
                  </VStack>
                  {value === option.value && (
                    <Icon as={FiCheck} color="blue.500" />
                  )}
                </HStack>
              </Box>
            ))
          ) : (
            <Box p={4} textAlign="center">
              <Text fontSize="sm" color="gray.500">
                No {isMonospace ? "variants" : "paths"} found matching "
                {searchQuery}"
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    )}
  </>
);

function BulkUpdateLinks() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { open: isOpen, onOpen, onClose } = useDisclosure();
  
  const [linkIds, setLinkIds] = useState("");
  const [formData, setFormData] = useState<FormData>({
    domain: "try.zown.ca",
    destinationType: "single",
    destinationPath: "",
    destinationVariant: "",
    customRedirectUrl: "",
    abTestPaths: [
      { id: "1", type: "path", value: "", percentage: 50 },
      { id: "2", type: "path", value: "", percentage: 50 }
    ],
    abTestSchedule: {
      enabled: false,
      startDate: "",
      endDate: ""
    }
  });
  
  const [urlValidation, setUrlValidation] = useState<{ isValid: boolean; error?: string }>({ isValid: true });
  
  const [updateResults, setUpdateResults] = useState<{
    success: string[];
    failed: string[];
    totalUpdated: number;
  } | null>(null);

  // Dropdown states
  const [pathSearchQuery, setPathSearchQuery] = useState("");
  const [isPathDropdownOpen, setIsPathDropdownOpen] = useState(false);
  const [variantSearchQuery, setVariantSearchQuery] = useState("");
  const [isVariantDropdownOpen, setIsVariantDropdownOpen] = useState(false);
  const [abTestDropdowns, setAbTestDropdowns] = useState<{
    [key: string]: { isOpen: boolean; searchQuery: string };
  }>({});

  // Fetch all links to validate IDs
  const { data: allLinks = [] } = useQuery({
    queryKey: ["links"],
    queryFn: mockApi.getLinks
  });

  // Populate IDs from navigation state if available
  useEffect(() => {
    if (location.state?.selectedIds && location.state.selectedIds.length > 0) {
      // Join the selected IDs with newlines for the textarea
      const idsText = location.state.selectedIds.join('\n');
      setLinkIds(idsText);
      
      // Clear the state to prevent re-population on page refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      if (isPathDropdownOpen && !target.closest("[data-path-dropdown]")) {
        setIsPathDropdownOpen(false);
        setPathSearchQuery("");
      }

      if (isVariantDropdownOpen && !target.closest("[data-variant-dropdown]")) {
        setIsVariantDropdownOpen(false);
        setVariantSearchQuery("");
      }

      Object.entries(abTestDropdowns).forEach(([pathId, dropdown]) => {
        if (
          dropdown.isOpen &&
          !target.closest(`[data-ab-dropdown="${pathId}"]`)
        ) {
          setAbTestDropdowns((prev) => ({
            ...prev,
            [pathId]: { ...prev[pathId], isOpen: false, searchQuery: "" }
          }));
        }
      });
    };

    const hasOpenDropdowns =
      isPathDropdownOpen ||
      isVariantDropdownOpen ||
      Object.values(abTestDropdowns).some((d) => d.isOpen);

    if (hasOpenDropdowns) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [isPathDropdownOpen, isVariantDropdownOpen, abTestDropdowns]);

  // Get main domain from tracking domain
  const getMainDomain = (trackingDomain: string) => {
    if (trackingDomain.includes("zown.ca")) {
      return "zown.ca";
    } else if (trackingDomain.includes("zownhomes.com")) {
      return "zownhomes.com";
    }
    return "zownhomes.com"; // Default fallback
  };

  // Filter paths based on search query
  const filteredPaths = availablePaths.filter(
    (path) =>
      path.value.toLowerCase().includes(pathSearchQuery.toLowerCase()) ||
      path.description.toLowerCase().includes(pathSearchQuery.toLowerCase())
  );

  // Filter variants based on search query
  const filteredVariants = availableVariants.filter(
    (variant) =>
      variant.value.toLowerCase().includes(variantSearchQuery.toLowerCase()) ||
      variant.description
        .toLowerCase()
        .includes(variantSearchQuery.toLowerCase())
  );

  // Parse link IDs from input
  const parseLinkIds = (input: string): string[] => {
    const lines = input
      .split(/[\n]+/)
      .map((line) => line.trim())
      .filter((line) => line.length > 0);

    return lines
      .map((line) => {
        line = line.replace(/^https?:\/\//, "");
        if (line.includes(".")) {
          const parts = line.split("/");
          return parts[parts.length - 1] || "";
        }
        return line;
      })
      .filter((id) => id.length > 0);
  };

  // Get valid and invalid link IDs
  const getValidationResults = () => {
    const inputIds = parseLinkIds(linkIds);
    const validIds: string[] = [];
    const invalidIds: string[] = [];

    inputIds.forEach((id) => {
      if (allLinks.some((link) => link.id === id || link.customId === id)) {
        validIds.push(id);
      } else {
        invalidIds.push(id);
      }
    });

    return { validIds, invalidIds };
  };

  const { validIds, invalidIds } = getValidationResults();

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: async () => {
      const successIds: string[] = [];
      const failedIds: string[] = [];

      // Prepare update data
      const updateData: any = {};
      
      if (formData.destinationType === "custom") {
        updateData.redirectUrl = formData.customRedirectUrl;
      } else {
        updateData.destinationType = formData.destinationType;
        updateData.domain = formData.domain;
        
        if (formData.destinationType === "single") {
          updateData.destinationPath = formData.destinationPath;
        } else if (formData.destinationType === "variant") {
          updateData.destinationVariant = formData.destinationVariant;
        } else if (formData.destinationType === "ab-test") {
          updateData.abTestPaths = formData.abTestPaths;
          updateData.abTestSchedule = formData.abTestSchedule;
        }
      }

      // Update each link
      await Promise.all(
        validIds.map(async (id) => {
          try {
            await mockApi.updateLink(id, updateData);
            successIds.push(id);
          } catch (error) {
            failedIds.push(id);
          }
        })
      );

      return { successIds, failedIds };
    },
    onSuccess: ({ successIds, failedIds }) => {
      queryClient.invalidateQueries({ queryKey: ["links"] });
      setUpdateResults({
        success: successIds,
        failed: failedIds,
        totalUpdated: successIds.length
      });
      onOpen();
    }
  });

  const handleFieldChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validIds.length === 0) {
      return;
    }
    
    if (formData.destinationType === "single" && !formData.destinationPath) {
      return;
    }
    if (formData.destinationType === "variant" && !formData.destinationVariant) {
      return;
    }
    if (formData.destinationType === "custom") {
      if (!formData.customRedirectUrl) {
        return;
      }
      // Validate custom URL before submission
      const validation = validateUrl(formData.customRedirectUrl);
      if (!validation.isValid) {
        setUrlValidation(validation);
        return;
      }
    }

    bulkUpdateMutation.mutate();
  };

  const handleReset = () => {
    setLinkIds("");
    setFormData({
      domain: "try.zown.ca",
      destinationType: "single",
      destinationPath: "",
      destinationVariant: "",
      customRedirectUrl: "",
      abTestPaths: [
        { id: "1", type: "path", value: "", percentage: 50 },
        { id: "2", type: "path", value: "", percentage: 50 }
      ],
      abTestSchedule: {
        enabled: false,
        startDate: "",
        endDate: ""
      }
    });
    setUpdateResults(null);
  };

  const handleContinue = () => {
    onClose();
    handleReset();
  };

  return (
    <Box bg="gray.50" minH="100vh">
      <Container maxW="6xl" py={8}>
        {/* Header */}
        <VStack gap={6} align="stretch" mb={8}>
          <HStack justify="space-between" align="center">
            <HStack gap={3}>
              <Link to="/links">
                <Button variant="ghost" size="sm" _hover={{ bg: "gray.100" }}>
                  <FiArrowLeft />
                  Back to Links
                </Button>
              </Link>
              <Text color="gray.500">/</Text>
              <Text color="gray.700" fontWeight="medium">
                Bulk Update Links
              </Text>
            </HStack>
            <Badge colorScheme="purple" px={3} py={1} rounded="full">
              <HStack gap={1}>
                <Icon as={FiRefreshCw} />
                <Text>Bulk Update</Text>
              </HStack>
            </Badge>
          </HStack>
        </VStack>

        <Grid templateColumns={{ base: "1fr", lg: "1fr 400px" }} gap={8}>
          {/* Main Form */}
          <VStack as="form" onSubmit={handleSubmit} gap={6} align="stretch">
            {/* Link IDs Selection */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiEdit} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Select Links to Update
                </Text>
                <Badge colorScheme="red" size="sm">
                  Required
                </Badge>
              </HStack>

              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontSize="sm" fontWeight="medium" mb={2}>
                    Link IDs or URLs
                  </Text>
                  <Textarea
                    placeholder="Enter link IDs in any of these formats:&#10;&#10;234234324&#10;go.zown.ca/234234324&#10;https://try.zown.ca/book&#10;http://try.zownhomes.ca/meet&#10;&#10;One entry per line"
                    value={linkIds}
                    onChange={(e) => setLinkIds(e.target.value)}
                    rows={8}
                    fontFamily="mono"
                    fontSize="sm"
                    bg="gray.50"
                    borderColor="gray.300"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{
                      borderColor: "blue.500",
                      boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)"
                    }}
                  />
                  <Text fontSize="sm" color="gray.500" mt={2}>
                    Enter one link ID or URL per line. Invalid IDs will be highlighted.
                  </Text>
                </Box>

                {/* Validation Results */}
                {linkIds && (
                  <VStack gap={3} align="stretch">
                    {validIds.length > 0 && (
                      <Box
                        p={3}
                        bg="green.50"
                        border="1px"
                        borderColor="green.200"
                        rounded="md"
                      >
                        <HStack gap={2} mb={2}>
                          <Icon as={FiCheckCircle} color="green.600" boxSize={4} />
                          <Text fontSize="sm" color="green.700" fontWeight="medium">
                            {validIds.length} valid link{validIds.length !== 1 ? "s" : ""} found
                          </Text>
                        </HStack>
                        <Box pl={6}>
                          <HStack gap={2} wrap="wrap">
                            {validIds.slice(0, 10).map((id) => (
                              <Badge key={id} colorScheme="green" size="sm">
                                {id}
                              </Badge>
                            ))}
                            {validIds.length > 10 && (
                              <Text fontSize="xs" color="green.600">
                                +{validIds.length - 10} more
                              </Text>
                            )}
                          </HStack>
                        </Box>
                      </Box>
                    )}

                    {invalidIds.length > 0 && (
                      <Box
                        p={3}
                        bg="orange.50"
                        border="1px"
                        borderColor="orange.200"
                        rounded="md"
                      >
                        <HStack gap={2} mb={2}>
                          <Icon as={FiAlertCircle} color="orange.600" boxSize={4} />
                          <Text fontSize="sm" color="orange.700" fontWeight="medium">
                            {invalidIds.length} invalid ID{invalidIds.length !== 1 ? "s" : ""}
                          </Text>
                        </HStack>
                        <Box pl={6}>
                          <HStack gap={2} wrap="wrap">
                            {invalidIds.slice(0, 10).map((id) => (
                              <Badge key={id} colorScheme="orange" size="sm">
                                {id}
                              </Badge>
                            ))}
                            {invalidIds.length > 10 && (
                              <Text fontSize="xs" color="orange.600">
                                +{invalidIds.length - 10} more
                              </Text>
                            )}
                          </HStack>
                        </Box>
                      </Box>
                    )}
                  </VStack>
                )}
              </VStack>
            </Box>

            {/* Destination Section with Custom URL option */}
            <Box bg="white" shadow="sm" rounded="lg" p={6}>
              <HStack mb={4}>
                <Icon as={FiNavigation} color="blue.500" boxSize={5} />
                <Text fontWeight="semibold" fontSize="lg">
                  Destination
                </Text>
                <Badge colorScheme="red" size="sm">
                  Required
                </Badge>
              </HStack>

              <VStack gap={4} align="stretch">
                {/* Destination Type Toggle */}
                <Box>
                  <HStack mb={3}>
                    <Text fontSize="sm" fontWeight="medium">
                      Destination Type
                    </Text>
                  </HStack>
                  <HStack gap={2} wrap="wrap">
                    <Button
                      size="sm"
                      variant={formData.destinationType === "single" ? "solid" : "outline"}
                      colorScheme={formData.destinationType === "single" ? "blue" : "gray"}
                      onClick={() => handleFieldChange("destinationType", "single")}
                      disabled={bulkUpdateMutation.isPending}
                    >
                      <HStack gap={1}>
                        <Icon as={FiLink} boxSize={4} />
                        <Text>Single Path</Text>
                      </HStack>
                    </Button>
                    <Button
                      size="sm"
                      variant={formData.destinationType === "ab-test" ? "solid" : "outline"}
                      colorScheme={formData.destinationType === "ab-test" ? "blue" : "gray"}
                      onClick={() => handleFieldChange("destinationType", "ab-test")}
                      disabled={bulkUpdateMutation.isPending}
                    >
                      <HStack gap={1}>
                        <Icon as={FiShuffle} boxSize={4} />
                        <Text>A/B Test</Text>
                      </HStack>
                    </Button>
                    <Button
                      size="sm"
                      variant={formData.destinationType === "variant" ? "solid" : "outline"}
                      colorScheme={formData.destinationType === "variant" ? "blue" : "gray"}
                      onClick={() => handleFieldChange("destinationType", "variant")}
                      disabled={bulkUpdateMutation.isPending}
                    >
                      <HStack gap={1}>
                        <Icon as={FiHash} boxSize={4} />
                        <Text>Variant ID</Text>
                      </HStack>
                    </Button>
                    <Button
                      size="sm"
                      variant={formData.destinationType === "custom" ? "solid" : "outline"}
                      colorScheme={formData.destinationType === "custom" ? "blue" : "gray"}
                      onClick={() => handleFieldChange("destinationType", "custom")}
                      disabled={bulkUpdateMutation.isPending}
                    >
                      <HStack gap={1}>
                        <Icon as={FiExternalLink} boxSize={4} />
                        <Text>Custom URL</Text>
                      </HStack>
                    </Button>
                  </HStack>
                </Box>

                {/* Single Path Selector */}
                {formData.destinationType === "single" && (
                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        Website Path
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" color="gray.600" minW="fit-content">
                        https://{getMainDomain(formData.domain)}
                      </Text>
                      <Box position="relative" flex={1} data-path-dropdown>
                        <SearchableDropdown
                          value={formData.destinationPath}
                          placeholder="Select a path..."
                          options={filteredPaths}
                          isOpen={isPathDropdownOpen}
                          onToggle={() => {
                            setIsPathDropdownOpen(!isPathDropdownOpen);
                            setPathSearchQuery("");
                          }}
                          onSelect={(value) => {
                            handleFieldChange("destinationPath", value);
                            setIsPathDropdownOpen(false);
                            setPathSearchQuery("");
                          }}
                          searchQuery={pathSearchQuery}
                          onSearchChange={setPathSearchQuery}
                          disabled={bulkUpdateMutation.isPending}
                        />
                      </Box>
                    </HStack>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Select from available paths or search by path name or description
                    </Text>
                  </Box>
                )}

                {/* Variant Selector */}
                {formData.destinationType === "variant" && (
                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        Website Variant ID
                      </Text>
                    </HStack>
                    <Box position="relative" data-variant-dropdown>
                      <SearchableDropdown
                        value={formData.destinationVariant}
                        placeholder="Select a variant..."
                        options={filteredVariants}
                        isOpen={isVariantDropdownOpen}
                        onToggle={() => {
                          setIsVariantDropdownOpen(!isVariantDropdownOpen);
                          setVariantSearchQuery("");
                        }}
                        onSelect={(value) => {
                          handleFieldChange("destinationVariant", value);
                          setIsVariantDropdownOpen(false);
                          setVariantSearchQuery("");
                        }}
                        searchQuery={variantSearchQuery}
                        onSearchChange={setVariantSearchQuery}
                        disabled={bulkUpdateMutation.isPending}
                        isMonospace={true}
                      />
                    </Box>
                    <Text fontSize="sm" color="gray.500" mt={2}>
                      Select a specific website variant by its ID for precise version control
                    </Text>

                    <Box
                      mt={3}
                      p={3}
                      bg="purple.50"
                      rounded="md"
                      borderLeft="3px solid"
                      borderColor="purple.400"
                    >
                      <HStack gap={2} align="start">
                        <Icon as={FiInfo} color="purple.600" boxSize={4} mt={0.5} />
                        <VStack align="start" gap={1}>
                          <Text fontSize="xs" color="purple.800" fontWeight="medium">
                            About Variant IDs:
                          </Text>
                          <Text fontSize="xs" color="purple.700">
                            • Each variant represents a specific version of a page
                          </Text>
                          <Text fontSize="xs" color="purple.700">
                            • IDs are generated by your CMS or testing platform
                          </Text>
                          <Text fontSize="xs" color="purple.700">
                            • Perfect for multivariate testing and version control
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </Box>
                )}

                {/* A/B Test Configuration */}
                {formData.destinationType === "ab-test" && (
                  <>
                    {/* Schedule Settings - Matching CreateABTest page */}
                    <VStack gap={4} align="stretch">
                      {/* Enable Scheduling Toggle */}
                      <Box>
                        <HStack mb={3}>
                          <Text fontSize="sm" fontWeight="medium">
                            Auto-schedule test activation
                          </Text>
                          <Box
                            position="relative"
                            display="inline-block"
                            width="44px"
                            height="24px"
                            bg={formData.abTestSchedule.enabled ? "blue.500" : "gray.300"}
                            borderRadius="full"
                            transition="all 0.3s"
                            cursor={bulkUpdateMutation.isPending ? "not-allowed" : "pointer"}
                            onClick={() => {
                              if (!bulkUpdateMutation.isPending) {
                                if (!formData.abTestSchedule.enabled) {
                                  // Enabling: set start date to current time
                                  const now = new Date();
                                  const year = now.getFullYear();
                                  const month = String(now.getMonth() + 1).padStart(2, "0");
                                  const day = String(now.getDate()).padStart(2, "0");
                                  const hours = String(now.getHours()).padStart(2, "0");
                                  const minutes = String(now.getMinutes()).padStart(2, "0");
                                  const currentDateTime = `${year}-${month}-${day}T${hours}:${minutes}`;
                                  
                                  handleFieldChange("abTestSchedule", {
                                    enabled: true,
                                    startDate: currentDateTime,
                                    endDate: ""
                                  });
                                } else {
                                  // Disabling: reset all dates
                                  handleFieldChange("abTestSchedule", {
                                    enabled: false,
                                    startDate: "",
                                    endDate: ""
                                  });
                                }
                              }
                            }}
                            _hover={{
                              bg: formData.abTestSchedule.enabled ? "blue.600" : "gray.400"
                            }}
                            _after={{
                              content: '""',
                              position: "absolute",
                              width: "20px",
                              height: "20px",
                              borderRadius: "full",
                              bg: "white",
                              top: "2px",
                              left: formData.abTestSchedule.enabled ? "22px" : "2px",
                              transition: "left 0.3s",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
                            }}
                          />
                        </HStack>
                        <Text fontSize="sm" color="gray.500">
                          Automatically start and stop the A/B test at specific times
                        </Text>
                      </Box>

                      {/* Schedule Configuration */}
                      {formData.abTestSchedule.enabled && (
                        <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                          <Box>
                            <Text fontSize="sm" fontWeight="medium" mb={2}>
                              Start Date & Time
                            </Text>
                            <Input
                              type="datetime-local"
                              value={formData.abTestSchedule.startDate}
                              onChange={(e) =>
                                handleFieldChange("abTestSchedule", {
                                  ...formData.abTestSchedule,
                                  startDate: e.target.value
                                })
                              }
                              size="md"
                              bg="white"
                              disabled={bulkUpdateMutation.isPending}
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Test will automatically become active at this time
                            </Text>
                          </Box>
                          <Box>
                            <HStack mb={2}>
                              <Text fontSize="sm" fontWeight="medium">
                                End Date & Time
                              </Text>
                              <Badge colorScheme="gray" size="sm">
                                Optional
                              </Badge>
                            </HStack>
                            <Input
                              type="datetime-local"
                              value={formData.abTestSchedule.endDate}
                              onChange={(e) =>
                                handleFieldChange("abTestSchedule", {
                                  ...formData.abTestSchedule,
                                  endDate: e.target.value
                                })
                              }
                              size="md"
                              bg="white"
                              placeholder="Leave empty to run indefinitely"
                              disabled={bulkUpdateMutation.isPending}
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Leave empty to run indefinitely, or set end time for automatic deactivation
                            </Text>
                          </Box>
                        </Grid>
                      )}

                      {/* Schedule Info */}
                      {formData.abTestSchedule.enabled && (
                        <Box
                          p={3}
                          bg="green.50"
                          rounded="md"
                          borderLeft="3px solid"
                          borderColor="green.400"
                        >
                          <HStack gap={2} align="start">
                            <Icon as={FiInfo} color="green.600" boxSize={4} mt={0.5} />
                            <VStack align="start" gap={1}>
                              <Text fontSize="sm" color="green.800" fontWeight="medium">
                                Automated Scheduling:
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                • Test will automatically activate at the start time
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                • Test will automatically deactivate at the end time (if specified)
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                • Leave end date empty to run indefinitely until manually stopped
                              </Text>
                              <Text fontSize="sm" color="green.700">
                                • Updates will apply these schedule settings to all selected links
                              </Text>
                            </VStack>
                          </HStack>
                        </Box>
                      )}

                      {/* Schedule Validation */}
                      {formData.abTestSchedule.enabled &&
                        formData.abTestSchedule.startDate &&
                        formData.abTestSchedule.endDate &&
                        new Date(formData.abTestSchedule.startDate) >= new Date(formData.abTestSchedule.endDate) && (
                        <Box
                          p={3}
                          bg="red.50"
                          rounded="md"
                          borderLeft="3px solid"
                          borderColor="red.400"
                        >
                          <HStack gap={2}>
                            <Icon as={FiInfo} color="red.600" boxSize={4} />
                            <Text fontSize="sm" color="red.800">
                              ⚠️ End date must be after start date
                            </Text>
                          </HStack>
                        </Box>
                      )}
                    </VStack>

                    {/* Test Paths */}
                    <ABTestPaths
                      paths={formData.abTestPaths}
                      onChange={(paths) => handleFieldChange("abTestPaths", paths)}
                      disabled={bulkUpdateMutation.isPending}
                      domain={formData.domain}
                      availablePaths={availablePaths}
                      availableVariants={availableVariants}
                      dropdownStates={abTestDropdowns}
                      onDropdownChange={(pathId, state) => {
                        setAbTestDropdowns((prev) => ({
                          ...prev,
                          [pathId]: state
                        }));
                      }}
                    />

                    <Box
                      p={3}
                      bg="blue.50"
                      rounded="md"
                      borderLeft="3px solid"
                      borderColor="blue.400"
                    >
                      <HStack gap={2} align="start">
                        <Icon as={FiInfo} color="blue.600" boxSize={4} mt={0.5} />
                        <VStack align="start" gap={1}>
                          <Text fontSize="xs" color="blue.800" fontWeight="medium">
                            Enhanced A/B Testing:
                          </Text>
                          <Text fontSize="xs" color="blue.700">
                            • Test multiple variations simultaneously
                          </Text>
                          <Text fontSize="xs" color="blue.700">
                            • Mix regular paths with variant IDs
                          </Text>
                          <Text fontSize="xs" color="blue.700">
                            • Schedule tests to run during specific periods
                          </Text>
                          <Text fontSize="xs" color="blue.700">
                            • Precise traffic distribution control
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </>
                )}

                {/* Custom URL Input */}
                {formData.destinationType === "custom" && (
                  <Box>
                    <HStack mb={2}>
                      <Text fontSize="sm" fontWeight="medium">
                        Custom Redirect URL
                      </Text>
                      {formData.customRedirectUrl && !urlValidation.isValid && (
                        <HStack gap={1}>
                          <Icon as={FiAlertCircle} color="red.500" boxSize={4} />
                          <Text fontSize="xs" color="red.500">
                            {urlValidation.error}
                          </Text>
                        </HStack>
                      )}
                      {formData.customRedirectUrl && urlValidation.isValid && (
                        <HStack gap={1}>
                          <Icon as={FiCheck} color="green.500" boxSize={4} />
                          <Text fontSize="xs" color="green.500">
                            Valid URL
                          </Text>
                        </HStack>
                      )}
                    </HStack>
                    <Input
                      placeholder="https://example.com/landing-page"
                      value={formData.customRedirectUrl}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleFieldChange("customRedirectUrl", value);
                        // Validate URL on change
                        if (value) {
                          const validation = validateUrl(value);
                          setUrlValidation(validation);
                        } else {
                          setUrlValidation({ isValid: true }); // Don't show error for empty field
                        }
                      }}
                      disabled={bulkUpdateMutation.isPending}
                      size="md"
                      bg="white"
                      borderColor={formData.customRedirectUrl && !urlValidation.isValid ? "red.500" : "gray.300"}
                      _hover={{ borderColor: formData.customRedirectUrl && !urlValidation.isValid ? "red.500" : "gray.400" }}
                      _focus={{
                        borderColor: formData.customRedirectUrl && !urlValidation.isValid ? "red.500" : "blue.500",
                        boxShadow: formData.customRedirectUrl && !urlValidation.isValid ? "0 0 0 1px var(--chakra-colors-red-500)" : "0 0 0 1px var(--chakra-colors-blue-500)"
                      }}
                    />
                    <Text fontSize="sm" color={formData.customRedirectUrl && !urlValidation.isValid ? "red.500" : "gray.500"} mt={2}>
                      {formData.customRedirectUrl && !urlValidation.isValid 
                        ? urlValidation.error 
                        : "Enter the full URL where all selected links should redirect to"}
                    </Text>
                    
                    <Box
                      mt={3}
                      p={3}
                      bg="amber.50"
                      rounded="md"
                      borderLeft="3px solid"
                      borderColor="amber.400"
                    >
                      <HStack gap={2} align="start">
                        <Icon as={FiInfo} color="amber.600" boxSize={4} mt={0.5} />
                        <VStack align="start" gap={1}>
                          <Text fontSize="xs" color="amber.800" fontWeight="medium">
                            Custom URL Notes:
                          </Text>
                          <Text fontSize="xs" color="amber.700">
                            • This will override any existing destination settings
                          </Text>
                          <Text fontSize="xs" color="amber.700">
                            • All selected links will redirect to this exact URL
                          </Text>
                          <Text fontSize="xs" color="amber.700">
                            • Make sure the URL is valid and accessible
                          </Text>
                        </VStack>
                      </HStack>
                    </Box>
                  </Box>
                )}
              </VStack>
            </Box>

            {/* Action Buttons */}
            <HStack gap={3}>
              <Button
                type="submit"
                colorScheme="blue"
                size="lg"
                disabled={
                  bulkUpdateMutation.isPending ||
                  validIds.length === 0 ||
                  (formData.destinationType === "single" && !formData.destinationPath) ||
                  (formData.destinationType === "variant" && !formData.destinationVariant) ||
                  (formData.destinationType === "custom" && !formData.customRedirectUrl)
                }
                width="full"
              >
                <HStack gap={2}>
                  {bulkUpdateMutation.isPending ? (
                    <>
                      <Icon as={FiLoader} className="animate-spin" />
                      <Text>Updating {validIds.length} Links...</Text>
                    </>
                  ) : (
                    <>
                      <Icon as={FiRefreshCw} />
                      <Text>Update {validIds.length} Link{validIds.length !== 1 ? "s" : ""}</Text>
                    </>
                  )}
                </HStack>
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={handleReset}
                disabled={bulkUpdateMutation.isPending}
              >
                Reset
              </Button>
            </HStack>
          </VStack>

          {/* Sidebar */}
          <VStack gap={6} align="stretch">
            {/* Update Preview */}
            <Box
              bg="white"
              shadow="sm"
              borderColor="gray.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="gray.600" boxSize={5} />
                <Text fontWeight="semibold" color="gray.900">
                  Update Preview
                </Text>
              </HStack>

              <VStack align="stretch" gap={3} fontSize="sm">
                <Box>
                  <Text color="gray.500" mb={1}>Links to Update:</Text>
                  <Text fontWeight="medium" color="gray.900">
                    {validIds.length} link{validIds.length !== 1 ? "s" : ""}
                  </Text>
                </Box>

                <Box>
                  <Text color="gray.500" mb={1}>Destination Type:</Text>
                  <Text fontWeight="medium" color="gray.900">
                    {formData.destinationType === "single" && "Single Path"}
                    {formData.destinationType === "ab-test" && "A/B Test"}
                    {formData.destinationType === "variant" && "Variant ID"}
                    {formData.destinationType === "custom" && "Custom URL"}
                  </Text>
                </Box>

                {formData.destinationType === "single" && formData.destinationPath && (
                  <Box>
                    <Text color="gray.500" mb={1}>Path:</Text>
                    <Text fontWeight="medium" color="gray.900" fontFamily="mono" fontSize="xs">
                      {formData.destinationPath}
                    </Text>
                  </Box>
                )}

                {formData.destinationType === "variant" && formData.destinationVariant && (
                  <Box>
                    <Text color="gray.500" mb={1}>Variant:</Text>
                    <Text fontWeight="medium" color="gray.900" fontFamily="mono" fontSize="xs">
                      {formData.destinationVariant}
                    </Text>
                  </Box>
                )}

                {formData.destinationType === "custom" && formData.customRedirectUrl && (
                  <Box>
                    <Text color="gray.500" mb={1}>Custom URL:</Text>
                    <Text fontWeight="medium" color="gray.900" fontSize="xs" wordBreak="break-all">
                      {formData.customRedirectUrl}
                    </Text>
                  </Box>
                )}

              </VStack>
            </Box>

            {/* Instructions */}
            <Box
              bg="blue.50"
              shadow="sm"
              borderColor="blue.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="blue.600" boxSize={5} />
                <Text fontWeight="semibold" color="blue.900">
                  How It Works
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="blue.800">
                <Text>1. Enter link IDs or URLs (one per line)</Text>
                <Text>2. Configure the destination settings</Text>
                <Text>3. Click Update to apply changes to all selected links</Text>
              </VStack>
            </Box>

            {/* Tips */}
            <Box
              bg="purple.50"
              shadow="sm"
              borderColor="purple.200"
              border="1px"
              rounded="lg"
              p={6}
            >
              <HStack mb={3}>
                <Icon as={FiInfo} color="purple.600" boxSize={5} />
                <Text fontWeight="semibold" color="purple.900">
                  Bulk Update Tips
                </Text>
              </HStack>

              <VStack align="stretch" gap={2} fontSize="sm" color="purple.800">
                <Text>• Updates are applied immediately</Text>
                <Text>• Link IDs remain unchanged</Text>
                <Text>• Historical data is preserved</Text>
                <Text>• Test with a few links first</Text>
              </VStack>
            </Box>
          </VStack>
        </Grid>

        {/* Success Dialog */}
        {isOpen && updateResults && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.600"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={9999}
          >
            <Box
              bg="white"
              rounded="lg"
              shadow="xl"
              p={8}
              maxW="md"
              width="90%"
            >
              <VStack gap={4} align="center">
                <Box
                  bg="green.100"
                  rounded="full"
                  p={3}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Icon as={FiCheck} color="green.600" boxSize={8} />
                </Box>
                <Text fontSize="2xl" fontWeight="bold">
                  Bulk Update Complete!
                </Text>
                
                <VStack gap={2} width="full">
                  {updateResults.success.length > 0 && (
                    <Box
                      bg="green.50"
                      p={3}
                      rounded="md"
                      width="full"
                      border="1px"
                      borderColor="green.200"
                    >
                      <HStack gap={2}>
                        <Icon as={FiCheckCircle} color="green.600" />
                        <Text fontSize="sm" color="green.700">
                          Successfully updated {updateResults.success.length} link{updateResults.success.length !== 1 ? "s" : ""}
                        </Text>
                      </HStack>
                    </Box>
                  )}
                  
                  {updateResults.failed.length > 0 && (
                    <Box
                      bg="red.50"
                      p={3}
                      rounded="md"
                      width="full"
                      border="1px"
                      borderColor="red.200"
                    >
                      <HStack gap={2}>
                        <Icon as={FiAlertCircle} color="red.600" />
                        <Text fontSize="sm" color="red.700">
                          Failed to update {updateResults.failed.length} link{updateResults.failed.length !== 1 ? "s" : ""}
                        </Text>
                      </HStack>
                    </Box>
                  )}
                </VStack>

                <HStack gap={3} width="full" mt={4}>
                  <Button variant="outline" onClick={handleContinue} flex={1}>
                    Update More Links
                  </Button>
                  <Button
                    colorScheme="blue"
                    onClick={() => navigate("/links")}
                    flex={1}
                  >
                    View All Links
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  );
}

// Add spinning animation
const style = document.createElement("style");
style.textContent = `
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }
`;
document.head.appendChild(style);

export default BulkUpdateLinks;